import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { getUserSignature } from '@dealstack/frontend/shared/dataExchange/services/god.service';

export const SAVED_SIGNATURE_QUERY_KEY: [string] = [API_PATHS.USER_SIGNATURE_GET];

const blobToDataUrl = (blob: Blob): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(String(reader.result));
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(blob);
	});

/**
 * The signed-URL download doesn't reliably carry an `image/*` Content-Type — S3
 * commonly serves it as `application/octet-stream`, which makes `response.blob()`
 * (and the resulting `FileReader` data URL) inherit that type. The backend then
 * rejects `drawnSignatureBase64` because it isn't `data:image/(png|jpeg);base64,...`.
 *
 * Read the raw bytes and re-tag the blob from the magic bytes (FF D8 FF → JPEG,
 * otherwise PNG) so the data URL always carries a valid image MIME type.
 */
const fetchSignatureAsImageDataUrl = async (signedUrl: string): Promise<string> => {
	const response = await fetch(signedUrl);
	if (!response.ok) {
		throw new Error('Failed to fetch saved signature');
	}
	const buffer = await response.arrayBuffer();
	const bytes = new Uint8Array(buffer);
	const isJpeg = bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
	const mimeType = isJpeg ? 'image/jpeg' : 'image/png';
	return blobToDataUrl(new Blob([buffer], { type: mimeType }));
};

export interface UseSavedSignatureResult {
	savedSignatureDataUrl: string | null;
	isPresent: boolean;
	isLoading: boolean;
	isError: boolean;
}

/**
 * @param enabled When false, the authenticated `/user/signature` query is not run.
 * Public/witness signers are unauthenticated, so firing this query 401s and triggers
 * a login redirect — callers in those flows must pass `enabled: false`.
 */
export const useSavedSignature = (enabled = true): UseSavedSignatureResult => {
	const summaryQuery = useQuery({
		queryKey: SAVED_SIGNATURE_QUERY_KEY,
		// getUserSignature returns undefined on 204; React Query v4 requires a non-undefined
		// return value, so we normalise undefined → null here.
		queryFn: async (ctx) => {
			const result = await getUserSignature(ctx.signal);
			return result ?? null;
		},
		enabled,
		staleTime: 60_000,
	});

	// summaryQuery.data is ApiV1.Response<UserSignatureRes> | null (null = 204, no signature)
	const summary = summaryQuery.data?.payload ?? null;

	const imageQuery = useQuery({
		queryKey: ['userSignature', 'image', summary?.signatureImageFileId],
		enabled: !!summary?.signedUrl,
		queryFn: () => fetchSignatureAsImageDataUrl(summary!.signedUrl),
		staleTime: 60_000,
	});

	// React Query v4: a disabled imageQuery has isLoading:true (status=loading, fetchStatus=idle).
	// Use isFetching for the image query so callers see isLoading:false when there is no signature.
	const imageIsLoading = imageQuery.isFetching || (imageQuery.isLoading && !!summary?.signedUrl);

	// React Query v4: a disabled query reports isLoading:true (status=loading,
	// fetchStatus=idle). Gate on `enabled` so a disabled hook reports isLoading:false.
	return {
		savedSignatureDataUrl: imageQuery.data ?? null,
		isPresent: !!summary && !!imageQuery.data,
		isLoading: (enabled && summaryQuery.isLoading) || imageIsLoading,
		isError: summaryQuery.isError || imageQuery.isError,
	};
};
